import {Avatar, Box, Typography, useTheme} from '@mui/material';
import {CampaignOverview} from '../../types';
import {CampaignStatus, getCampaignStatus, getClosingDay} from '../../utils/campaign';
import GroupsIcon from '@mui/icons-material/Groups';
import FlagIcon from '@mui/icons-material/Flag';
import {Colors} from '../common/colors';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import TagElement from './tag';
import {HistoryButton, MyLink} from '../common/button';
import Indicator from './indicator';
import {useResponsive} from '../../provider/MediaQueryProvider';
import Link from 'next/link';
import {getLocaleLabels} from '../../utils/lang';
import {RATIO_IMAGE} from '../../utils/settings';
import Image from 'next/image';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import {useTranslation} from 'next-i18next';

type CampaignHistoryProps = {
    campaign: CampaignOverview;
};

const IMG_WIDTH_DESKTOP = 640;
const IMG_WIDTH_MOBILE = 320;

export default function CampaignHistory(props: CampaignHistoryProps) {
    const theme = useTheme();
    const isDark = theme.palette.mode == 'dark';
    const {t, i18n} = useTranslation('common');
    const {isMobile, isTablet, isLaptop} = useResponsive();

    if (!props.campaign) {
        return <></>;
    }

    const campaign = props.campaign;

    const status = getCampaignStatus(campaign);
    const enabled = status == CampaignStatus.AFTER ;
    if (!enabled) {
        return <></>;
    }

    const labels = getLocaleLabels(campaign.labels, i18n.language);
    const picture = labels.cover_url;

    const authorPicture = campaign.author.picture_url;

    const closingDay = getClosingDay(campaign);

    const blocDirection = isLaptop || isTablet || isMobile ? 'column' : 'row';
    const itemDirection = isTablet || isMobile ? 'column' : 'row';
    const imgWidth = isTablet || isMobile ? IMG_WIDTH_MOBILE : IMG_WIDTH_DESKTOP;

    return (
        <Box sx={{paddingTop: isMobile ? '25px' : '50px', paddingBottom: isMobile ? '25px' : '50px'}}>
            <Box display="flex" flexDirection="row" justifyContent="center">
                <Box
                    display="flex"
                    flexDirection={blocDirection}
                    justifyContent="center"
                    sx={{
                        WebkitBoxShadow: Colors.shadow,
                        boxShadow: Colors.shadow,
                        borderRadius: '8px',
                    }}
                >
                    {Boolean(picture) && (
                        <Image
                            src={picture}
                            alt={labels.title}
                            width={imgWidth}
                            height={Math.floor(imgWidth / RATIO_IMAGE)}
                            style={{
                                borderTopLeftRadius: '8px',
                                borderBottomLeftRadius:
                                    isLaptop || isTablet || isMobile ? '0px' : '8px',
                                borderTopRightRadius:
                                    isLaptop || isTablet || isMobile ? '8px' : '0px',
                            }}
                            priority
                        />
                    )}
                    {!Boolean(picture) && (
                        <HideImageOutlinedIcon
                            sx={{
                                width: imgWidth,
                                height: Math.floor(imgWidth / RATIO_IMAGE),
                                display: 'block',
                                margin: 'auto',
                                borderTopLeftRadius: '8px',
                                borderBottomLeftRadius:
                                    isLaptop || isTablet || isMobile ? '0px' : '8px',
                                borderTopRightRadius:
                                    isLaptop || isTablet || isMobile ? '8px' : '0px',
                            }}
                        />
                    )}
                    <Box
                        padding={'15px'}
                        sx={{
                            borderBottomLeftRadius:
                                isLaptop || isTablet || isMobile ? '8px' : '0px',
                            borderTopRightRadius:
                                isLaptop || isTablet || isMobile ? '0px' : '8px',
                            borderBottomRightRadius: '8px',
                            width: imgWidth,
                            backgroundColor: isDark ? Colors.dark : 'white',
                        }}
                        display="flex"
                        flexDirection="column"
                        justifyContent="space-between"
                    >
                        <Box
                            sx={{
                                marginLeft: isMobile || isTablet ? '10px' : '40px',
                                marginRight: isMobile || isTablet ? '10px' : '40px',
                            }}
                            display="flex"
                            flex="1"
                            flexDirection="column"
                            justifyContent="space-between"
                        >
                            <Box
                                display="flex"
                                flexDirection="row"
                                marginTop={'10px'}
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Box display="flex" flexDirection="row">
                                    <Link
                                        href={'/author/' + campaign.author.url}
                                        style={{textDecoration: 'none'}}
                                    >
                                        <Avatar
                                            alt={campaign.author.display_name}
                                            src={authorPicture}
                                            imgProps={{referrerPolicy: 'no-referrer'}}
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                marginRight: '5px',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    </Link>
                                    <MyLink
                                        href={'/author/' + campaign.author.url}
                                        title={campaign.author.display_name}
                                        sx={{maxWidth: {sx: '250px', md: '300px'}}}
                                    />
                                </Box>
                                <Box display="flex" flexDirection="row" alignSelf="center">
                                    <LocalOfferIcon
                                        sx={{marginRight: '5px', fontSize: '32px'}}
                                    />
                                    {campaign.tags.map((t) => (
                                        <TagElement
                                            tag={t.tag}
                                            key={t.tag.tag_id}
                                            sx={{sx: '250px', md: '150px'}}
                                        />
                                    ))}
                                </Box>
                            </Box>
                            <Typography
                                sx={{
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    marginTop: '10px',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                }}
                            >
                                {labels.title}
                            </Typography>
                            <Typography
                                sx={{
                                    textAlign: 'justify',
                                    marginTop: '10px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 3,
                                    WebkitBoxOrient: 'vertical',
                                }}
                            >
                                {labels.subtitle}
                            </Typography>
                            <Box
                                display="flex"
                                flexDirection={itemDirection}
                                justifyContent="space-evenly"
                                marginTop={'10px'}
                            >
                                <Indicator
                                    text={campaign.contribution.toString()}
                                    icon={
                                        <GroupsIcon
                                            sx={{alignSelf: 'center', color: Colors.first}}
                                            fontSize="large"
                                        />
                                    }
                                />
                                <Indicator
                                    text = { t('campaign.finished') + ' ' + Intl.DateTimeFormat(i18n.language, {dateStyle: 'medium'}).format(closingDay) }
                                    icon={
                                        <FlagIcon
                                            sx={{alignSelf: 'center', color: Colors.first}}
                                            fontSize="large"
                                        />
                                    }
                                />
                            </Box>
                            <Box
                                marginTop='10px'
                                marginBottom='25px'
                                marginLeft={isMobile || isTablet ? '0px' : '40px'}
                                marginRight={isMobile || isTablet ? '0px' : '40px'}
                            >
                                <HistoryButton href={'/' + campaign.url}>
                                    {t('action.go_to_campaign')}
                                </HistoryButton>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}